import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import {
  getCalendarEventItemUsersQuery,
  getOrganizationMemberCountsQuery,
} from "../modules/MembersQueries";

/**
 * Fetches member IDs for a single school using its selected entity IDs
 * (grade/course IDs that belong to this school).
 */
const useSchoolMembers = (organizationId, entityIds, userTypeFilter, skip) => {
  const { data, loading } = useQuery(getCalendarEventItemUsersQuery, {
    variables: {
      organizationId,
      filters: {
        curriculumProgramIds: entityIds,
        userType: userTypeFilter,
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !organizationId || _.isEmpty(entityIds) || skip,
    context: { useHttpLink: true },
  });

  const memberIds = useMemo(() => {
    const edges = _.get(data, "node.calendarEventItemUsers.edges", []);
    return _.map(edges, edge => _.get(edge, "node.id"));
  }, [data]);

  return { memberIds, loading: loading && _.isEmpty(data) };
};

/**
 * Fetches member IDs for a single school using its selected entity IDs
 * (grade/course IDs that belong to this school).
 */
const useSchoolMembersCounts = organizationId => {
  const { data, loading } = useQuery(getOrganizationMemberCountsQuery, {
    variables: {
      organizationId,
    },
    fetchPolicy: "cache-and-network",
    skip: !organizationId,
    context: { useHttpLink: true },
  });

  const userCountMapGroupBySchool = useMemo(() => {
    const curriculumPrograms = _.get(data, "node.curriculumPrograms", []);
    return _.reduce(
      curriculumPrograms,
      (result, curriculumProgram) => {
        const schoolId = _.get(curriculumProgram, "id");
        if (schoolId) {
          const familyTotal = _.get(
            curriculumProgram,
            "familyMembers.totalCount",
            0
          );
          result[schoolId] = {
            studentCount: _.get(curriculumProgram, "students.totalCount", 0),
            staffCount: _.get(curriculumProgram, "staff.totalCount", 0),
            familyCount: familyTotal,
            familyMemberCount: familyTotal,
          };
        }
        return result;
      },
      {}
    );
  }, [data]);

  return {
    schoolMembersCountMap: userCountMapGroupBySchool,
    loading: loading && _.isEmpty(data),
  };
};

export default useSchoolMembersCounts;
